import Cookies from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState } from '../bsc-global-state/core';
import { DEFAULT_LOG_LEVEL, LogLevel, packageLogLevelStringToEnum } from './log-level';

/**
 * Returns the active log level for the BSC Logger, resolved from (in order):
 * developer cookie override, consumer init param, or `DEFAULT_LOG_LEVEL`.
 *
 * The first call resolves and caches; subsequent calls return the cached
 * value. `initializeGlobalState` invalidates the cache via
 * `resetLogLevelCache()` so an init that runs after the first Logger call
 * still picks up the consumer's `packageLogLevel`.
 *
 * SSR caveat: `cachedLogLevel` is a module-level singleton, so in a shared
 * Node module cache it persists across requests. Both `initializeGlobalState`
 * and `clearGlobalState` call `resetLogLevelCache()`, so per-request init and
 * teardown keep it correct — one request's resolved level cannot leak into the
 * next.
 */
let cachedLogLevel: LogLevel | undefined;

export const determineLogLevel = (): LogLevel => {
	if (cachedLogLevel !== undefined) {
		return cachedLogLevel;
	}
	cachedLogLevel = readDebugCookieOverride() ?? readInitParamLogLevel() ?? DEFAULT_LOG_LEVEL;
	return cachedLogLevel;
};

/** Clears the resolved-level cache so the next `determineLogLevel()` call re-resolves. */
export const resetLogLevelCache = (): void => {
	cachedLogLevel = undefined;
};

/**
 * Returns `LogLevel.DEBUG` when the dev override is active (cookie set AND
 * the safety-net FF is enabled), else `null`. The FF gate lets us remotely
 * disable the cookie path if it ever misbehaves.
 *
 * The cookie is checked BEFORE the feature gate on purpose: `fg` is only ever
 * evaluated when the (rare, dev-only) `atl-bsc-debug-mode` cookie is present.
 * This keeps `Logger.*` calls — including ones that run at module-load time
 * (e.g. the AtlIndexedDb singleton) — from calling `fg` during a plain import.
 * Otherwise importing BSC crashes any consumer whose test mocks `fg` with a
 * not-yet-initialized factory variable ("Cannot access 'mockFg' before
 * initialization").
 */
const readDebugCookieOverride = (): LogLevel | null => {
	let debugCookieSet = false;
	try {
		debugCookieSet = Cookies.get('atl-bsc-debug-mode') === 'true';
	} catch {
		// Cookie access can throw in sandboxed iframes / strict cookie policies.
		return null;
	}
	if (!debugCookieSet) {
		return null;
	}
	return fg('platform_moonjelly_bsc_debug_mode') ? LogLevel.DEBUG : null;
};

/**
 * Returns the LogLevel corresponding to the consumer's `packageLogLevel` init
 * param, or `null` if unset or unrecognized.
 */
const readInitParamLogLevel = (): LogLevel | null => {
	return packageLogLevelStringToEnum(getBscGlobalState().packageLogLevel);
};
